import React, { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery
} from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ShareIcon from '@mui/icons-material/Share';
import { toast } from 'react-toastify';
import { getPDF } from '../../../../../store/pdf/api';
import AppContentContainer from '../../../../../components/AppContentContainer/AppContentContainer';
import LeavePageConfirm from '../../../../../components/LeavePageConfirm/LeavePageConfirm';
import IconToast from '../../../../../components/NewToast';
import { isSharable, share } from '../../../../../utils/native/share';
import NativeOrWeb from '../../../../../utils/native';
import { iOS as isIOSDevice, safari as isSafariBrowser } from '../../../../../utils/device';
import { getReferralLink } from '../../../../../utils/referral';
import { getStudioReferralSource, trackStudioAction, type StudioTrackingSource } from '../utils/studioTracking';
import { getIsChubbUser } from '../../../../../utils/user';
import {
  DEFAULT_STUDIO_THEME_COLOR,
  STUDIO_FORMATS,
  STUDIO_LANGUAGES,
  STUDIO_TEMPLATES,
  type StudioPreferences,
  type StudioPreferencesStore,
  type StudioSettings
} from '../constants';
import { ensureStudioFonts } from '../utils/fonts';
import { getStudioExportFileName } from '../utils/fileName';
import { findStudioPackEntry } from '../utils/studioPacks';
import {
  areStudioPreferencesEqual,
  buildStudioPreferencesSnapshot,
  ensureStudioPreferenceSlots,
  getActiveStudioPreferences,
  persistStudioPreferencesStore,
  readStudioPreferencesStore,
  serializeStudioPreferencesStore
} from '../utils/studioPreferences';
import MobileToolBar from './MobileToolBar';
import PageNavigator from './PageNavigator';
import StudioCanvas from './Canvas/StudioCanvas';
import StudioSidebar from './Sidebar/StudioSidebar';
import StudioExportLoading from './StudioExportLoading';
import './style.scss';

const PAGE_STYLE: React.CSSProperties = {
  maxWidth: 'none',
  paddingTop: 45,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0
};

type WebReadyAction = 'share' | 'save';

const WebShareReadyDialog: React.FC<{
  intl: any;
  open: boolean;
  action: WebReadyAction | null;
  onClose: () => void;
  onShare: () => void;
}> = ({
  intl,
  open,
  action,
  onClose,
  onShare
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    slotProps={{
      paper: { className: 'studio-web-share-dialog' },
      backdrop: { className: 'studio-web-share-backdrop' }
    }}
  >
    <DialogTitle>{intl.formatMessage({ id: 'hot-topic-studio-share-ready-title' })}</DialogTitle>
    <DialogContent>
      <Typography>
        {intl.formatMessage({
          id: action === 'save' ? 'hot-topic-studio-save-ready-description' : 'hot-topic-studio-share-ready-description'
        })}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button className="studio-web-share-cancel" onClick={onClose}>
        {intl.formatMessage({ id: 'Cancel' })}
      </Button>
      <Button className="studio-web-share-confirm" variant="contained" onClick={onShare}>
        {intl.formatMessage({ id: action === 'save' ? 'hot-topic-studio-save-all' : 'Share' })}
      </Button>
    </DialogActions>
  </Dialog>
);

const getLocalizedTitle = (item: any, locale: string): string =>
  (item && item.title && (item.title[locale] || item.title['zh-Hant-HK'] || item.title.en)) || '';

const getDefaultStudioSettings = (locale: string, preferences?: Partial<StudioPreferences>): StudioSettings => ({
  locale:
    preferences && STUDIO_LANGUAGES.some(language => language.id === preferences.locale) ? preferences.locale! : locale,
  format: preferences && STUDIO_FORMATS[preferences.format as keyof typeof STUDIO_FORMATS] ? preferences.format! : 'post',
  template:
    preferences && STUDIO_TEMPLATES.some(template => template.id === preferences.template) ? preferences.template! : 'classic',
  themeColor: preferences?.themeColor || '',
  profileName: preferences?.profileName || '',
  watermarkLogo: preferences?.watermarkLogo || null
});

const getSlotNameDraft = (store: StudioPreferencesStore, slotIndex: number): string =>
  store.slots[slotIndex]?.name?.trim() || '';

const createPreferencesSnapshot = (
  settings: Pick<StudioSettings, 'locale' | 'format' | 'template' | 'profileName' | 'watermarkLogo'>,
  themeColor: string,
  name: string
): StudioPreferences =>
  buildStudioPreferencesSnapshot({
    name,
    locale: settings.locale,
    format: settings.format,
    template: settings.template,
    themeColor,
    profileName: settings.profileName,
    watermarkLogo: settings.watermarkLogo
  });

interface StudioEditorProps {
  intl: any;
  history: any;
  match: { params: { templateId?: string; newsId?: string; id?: string } };
  hotTopic?: any;
  userDetails: any;
  teamCampaignCodes: any[];
  teams: any[];
  renderHeaderButton: (show: boolean) => void;
  updateUserDetails: (data: any) => void;
  studioItem?: any;
  notFoundBackPath?: string;
  trackingSource?: StudioTrackingSource;
}

const StudioEditor: React.FC<StudioEditorProps> = ({
  intl,
  history,
  match,
  hotTopic,
  userDetails,
  teamCampaignCodes,
  teams,
  renderHeaderButton,
  updateUserDetails,
  studioItem,
  notFoundBackPath,
  trackingSource
}) => {
  const isMobile = useMediaQuery('(max-width:767px)');
  const sliderRef = useRef<any>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [pendingWebAction, setPendingWebAction] = useState<{ action: WebReadyAction; files: File[] } | null>(null);
  const [pendingSlotSwitch, setPendingSlotSwitch] = useState<number | null>(null);
  const [footerNavHeight, setFooterNavHeight] = useState(60);
  const templateId = match.params.templateId || match.params.newsId || (studioItem && String(studioItem.id)) || '';
  const useInjectedStudioItem = studioItem != null || notFoundBackPath != null;

  const item = useMemo(() => {
    if (studioItem) {
      return studioItem;
    }
    if (useInjectedStudioItem) {
      return undefined;
    }
    return findStudioPackEntry((hotTopic && hotTopic.data) || [], templateId);
  }, [hotTopic, studioItem, templateId, useInjectedStudioItem]);
  const pages = useMemo(() => (item && item.studioPackPages) || [], [item]);
  const initialLocale = STUDIO_LANGUAGES.some(language => language.id === intl.locale) ? intl.locale : 'zh-Hant-HK';
  const savedPreferencesApplied = useRef(false);
  const initialPreferencesStore = readStudioPreferencesStore(userDetails);
  const [preferencesStore, setPreferencesStore] = useState<StudioPreferencesStore>(initialPreferencesStore);
  const [selectedSlot, setSelectedSlot] = useState(initialPreferencesStore.activeSlot);
  const [slotName, setSlotName] = useState(getSlotNameDraft(initialPreferencesStore, initialPreferencesStore.activeSlot));
  const [settings, setSettings] = useState<StudioSettings>(() =>
    getDefaultStudioSettings(initialLocale, getActiveStudioPreferences(initialPreferencesStore))
  );
  const [cleanPreferencesSnapshot, setCleanPreferencesSnapshot] = useState<StudioPreferences>(() => {
    const initialSettings = getDefaultStudioSettings(initialLocale, getActiveStudioPreferences(initialPreferencesStore));
    return createPreferencesSnapshot(
      initialSettings,
      initialSettings.themeColor,
      getSlotNameDraft(initialPreferencesStore, initialPreferencesStore.activeSlot)
    );
  });
  const isChubbUser = getIsChubbUser();
  const hideProfile = isChubbUser;
  const renderSettings = useMemo<StudioSettings>(
    () => ({
      ...settings,
      themeColor: settings.themeColor || (item && item.studioPackThemeColor) || DEFAULT_STUDIO_THEME_COLOR,
      pageCount: pages.length,
      // Match app-wide Chubb detection (ENTCBB campaign + CBB / CBBTrainer / CBBTest teams).
      showChubbDisclaimer: isChubbUser,
      // Chubb users should not see or apply personal notes on studio output.
      profileName: hideProfile ? '' : settings.profileName,
      // Profile+ URL is computed here so the Lambda template (no Redux) can
      // render the same last-page QR as the live preview.
      qrCodeUrl:
        getReferralLink({
          targetAdviser: userDetails,
          client: undefined,
          source: getStudioReferralSource(trackingSource),
          medium: undefined,
          role: 'adviser',
          isQRCode: true,
          userDetails,
          isInternalRoute: undefined,
          extraSearch: templateId ? { htid: templateId } : undefined
        }) || undefined
    }),
    [hideProfile, isChubbUser, item, pages.length, settings, templateId, trackingSource, userDetails]
  );
  const currentPreferencesSnapshot = useMemo(
    () => createPreferencesSnapshot(settings, settings.themeColor, slotName),
    [settings, slotName]
  );
  // Works for both filled slots and empty slots ("空位"): any edit after the
  // last clean baseline (load / save / apply slot) counts as unsaved.
  const hasUnsavedLayoutChanges = !areStudioPreferencesEqual(
    currentPreferencesSnapshot,
    cleanPreferencesSnapshot
  );
  const selectedSlotIsEmpty = !preferencesStore.slots[selectedSlot];

  useEffect(() => {
    renderHeaderButton(false);
  }, [renderHeaderButton]);

  // User details can arrive after this route mounts. Apply their saved defaults
  // once, without replacing any settings they adjust during the session.
  useEffect(() => {
    if (!savedPreferencesApplied.current && userDetails) {
      const store = readStudioPreferencesStore(userDetails);
      const nextSettings = getDefaultStudioSettings(initialLocale, getActiveStudioPreferences(store));
      const nextSlotName = getSlotNameDraft(store, store.activeSlot);
      setPreferencesStore(store);
      setSelectedSlot(store.activeSlot);
      setSlotName(nextSlotName);
      setSettings(nextSettings);
      setCleanPreferencesSnapshot(createPreferencesSnapshot(nextSettings, nextSettings.themeColor, nextSlotName));
      savedPreferencesApplied.current = true;
    }
  }, [initialLocale, userDetails]);

  // Load the studio webfonts so the on-screen preview uses the same faces the
  // server-side render (Lambda Chromium) will use, keeping download == preview.
  useEffect(() => {
    ensureStudioFonts();
  }, []);

  // Desktop studio should not show the bottom FooterNav (same as the original layout).
  // Side-nav mode (width >= 960) keeps .footer-nav.side-nav visible.
  useEffect(() => {
    if (isMobile) {
      return undefined;
    }
    document.body.classList.add('hot-topic-studio-desktop');
    return () => document.body.classList.remove('hot-topic-studio-desktop');
  }, [isMobile]);

  // The browser version keeps the app's FooterNav visible. Its height is not
  // a fixed 50px: the centred Home button and device safe area make it taller
  // on some iOS/Android devices. Measure it so the dock is a real layout row,
  // never an area hidden below the fixed navigation.
  useEffect(() => {
    if (!isMobile) return undefined;

    const footerNav = document.querySelector('.footer-nav:not(.side-nav)') as HTMLElement | null;
    if (!footerNav) return undefined;

    const updateFooterNavHeight = () => setFooterNavHeight(Math.ceil(footerNav.getBoundingClientRect().height));
    updateFooterNavHeight();

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateFooterNavHeight);
    resizeObserver && resizeObserver.observe(footerNav);
    window.addEventListener('resize', updateFooterNavHeight);

    return () => {
      resizeObserver && resizeObserver.disconnect();
      window.removeEventListener('resize', updateFooterNavHeight);
    };
  }, [isMobile]);

  useEffect(() => {
    trackStudioAction(trackingSource, 'viewed', { id: templateId });
  }, [templateId, trackingSource]);

  const updateSettings = (partialSettings: Partial<StudioSettings>) => {
    setSettings(previous => ({ ...previous, ...partialSettings }));
  };

  const changeSlotName = (name: string) => {
    setSlotName(name);
  };

  const commitPreferencesStore = (nextStore: StudioPreferencesStore) => {
    const normalizedStore: StudioPreferencesStore = {
      activeSlot: nextStore.activeSlot,
      slots: ensureStudioPreferenceSlots(nextStore.slots)
    };
    setPreferencesStore(normalizedStore);
    persistStudioPreferencesStore(userDetails?._id, normalizedStore);
    // Serialize empty seats as markers (not JSON nulls) so one delete cannot be
    // misread as "all slots empty" after a Mongo/API round-trip.
    updateUserDetails({ hotTopicStudioPreferences: serializeStudioPreferencesStore(normalizedStore) });
  };

  const applyPreferenceSlot = (slotIndex: number, store: StudioPreferencesStore = preferencesStore) => {
    setSelectedSlot(slotIndex);
    const nextSlotName = getSlotNameDraft(store, slotIndex);
    setSlotName(nextSlotName);

    const slot = store.slots[slotIndex];
    if (!slot) {
      // Empty slot keeps the current canvas draft. Only reset the clean baseline
      // when there are no pending edits — otherwise leave-prompt would be lost.
      if (!hasUnsavedLayoutChanges) {
        setCleanPreferencesSnapshot(createPreferencesSnapshot(settings, settings.themeColor, nextSlotName));
      }
      return;
    }

    const nextSettings = getDefaultStudioSettings(initialLocale, slot);
    setSettings(nextSettings);
    setCleanPreferencesSnapshot(createPreferencesSnapshot(nextSettings, nextSettings.themeColor, nextSlotName));
    if (store.activeSlot !== slotIndex) {
      commitPreferencesStore({ ...store, activeSlot: slotIndex });
    }
  };

  const selectPreferenceSlot = (slotIndex: number) => {
    if (slotIndex === selectedSlot) {
      return;
    }

    const slot = preferencesStore.slots[slotIndex];
    // Applying a filled slot replaces the canvas. Confirm first so unsaved edits
    // are not silently discarded on desktop or mobile.
    if (slot && hasUnsavedLayoutChanges) {
      setPendingSlotSwitch(slotIndex);
      return;
    }

    applyPreferenceSlot(slotIndex);
  };

  const savePreferences = (): StudioPreferencesStore => {
    const preferences = createPreferencesSnapshot(settings, renderSettings.themeColor, slotName);
    const nextSlots = ensureStudioPreferenceSlots(preferencesStore.slots);
    nextSlots[selectedSlot] = preferences;
    const nextStore: StudioPreferencesStore = {
      activeSlot: selectedSlot,
      slots: nextSlots
    };
    commitPreferencesStore(nextStore);
    setSettings(previous => ({ ...previous, themeColor: preferences.themeColor }));
    setCleanPreferencesSnapshot(preferences);
    toast.info(IconToast('success', intl.formatMessage({ id: 'hot-topic-studio-layout-saved' })), {
      className: 'new-toast'
    });
    return nextStore;
  };

  const renamePreferenceSlot = () => {
    const currentSlot = preferencesStore.slots[selectedSlot];
    if (!currentSlot) {
      return;
    }

    const nextName = slotName.trim();
    if ((currentSlot.name || '') === nextName) {
      return;
    }

    const nextSlots = ensureStudioPreferenceSlots(preferencesStore.slots);
    nextSlots[selectedSlot] = { ...currentSlot, name: nextName };
    commitPreferencesStore({
      ...preferencesStore,
      slots: nextSlots
    });
    setCleanPreferencesSnapshot(createPreferencesSnapshot(settings, settings.themeColor, nextName));
  };

  const clearPreferenceSlot = () => {
    const slotToClear = selectedSlot;
    const nextSlots = ensureStudioPreferenceSlots(preferencesStore.slots);
    // Only clear the selected seat — never wipe the other saved layouts.
    nextSlots[slotToClear] = null;
    const remainingFilled = nextSlots.reduce((count, slot) => count + (slot ? 1 : 0), 0);
    const firstFilled = nextSlots.findIndex(Boolean);
    const nextStore: StudioPreferencesStore = {
      activeSlot: firstFilled >= 0 ? firstFilled : 0,
      slots: nextSlots
    };

    // Always persist the 3-seat store (including empty markers). Avoid unsetKey,
    // which raced with in-flight saves and could make the UI look fully wiped.
    commitPreferencesStore(nextStore);

    if (remainingFilled === 0) {
      const nextSettings = getDefaultStudioSettings(initialLocale);
      setSelectedSlot(0);
      setSlotName('');
      setSettings(nextSettings);
      setCleanPreferencesSnapshot(createPreferencesSnapshot(nextSettings, nextSettings.themeColor, ''));
      return;
    }

    const nextSlotName = getSlotNameDraft(nextStore, nextStore.activeSlot);
    const nextSettings = getDefaultStudioSettings(initialLocale, getActiveStudioPreferences(nextStore));
    setSelectedSlot(nextStore.activeSlot);
    setSlotName(nextSlotName);
    setSettings(nextSettings);
    setCleanPreferencesSnapshot(createPreferencesSnapshot(nextSettings, nextSettings.themeColor, nextSlotName));
  };

  const changePage = (index: number) => {
    setPageIndex(index);
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  // Render one page server-side (real Chrome renders the identical StudioPage,
  // so the download matches the live preview pixel-for-pixel). There is no
  // client-side fallback: html2canvas cannot reproduce blur filters, layered/
  // radial gradients or vertical text, so it would silently ship a different
  // image. On failure we surface the error toast instead.
  const renderPageBlob = (index: number): Promise<Blob> =>
    getPDF('hot-topic-studio', {
      page: pages[index],
      settings: renderSettings,
      pageNumber: index + 1,
      outputFormat: 'jpg'
    });

  // Fire every page Lambda at once. Promise.all keeps the returned blobs in
  // the same order as `indexes`, even when later pages finish first.
  const renderPageBlobs = (indexes: number[]): Promise<Blob[]> => {
    const total = indexes.length || 1;
    let completed = 0;
    return Promise.all(
      indexes.map(index =>
        renderPageBlob(index).then(blob => {
          completed += 1;
          setExportProgress(Math.round((completed / total) * 99));
          return blob;
        })
      )
    );
  };

  const downloadPages = async (indexes: number[]) => {
    setExportProgress(0);
    setExporting(true);
    // Safari (desktop + iOS) and other iOS browsers allow only one
    // programmatic download per click. Collect every page, then prompt the
    // user to tap again so the native share sheet can save the complete pack.
    const useWebShareSave =
      NativeOrWeb.getPlatform() === 'web' && (isIOSDevice() || isSafariBrowser());
    const webFiles: File[] = [];

    try {
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const exportTitle =
        getLocalizedTitle(item, settings.locale) || getLocalizedTitle(item, intl.locale) || templateId;

      const blobs = await renderPageBlobs(indexes);
      const exportedAt = new Date();
      for (let position = 0; position < indexes.length; position += 1) {
        const index = indexes[position];
        const blob = blobs[position];
        const filename = getStudioExportFileName(exportTitle, index + 1, blob.type, exportedAt);

        if (useWebShareSave) {
          webFiles.push(new File([blob], filename, { type: blob.type || 'image/jpeg' }));
        } else {
          await NativeOrWeb.saveImageToPhotoLibrary(blob, filename);
        }
      }

      if (useWebShareSave) {
        setPendingWebAction({ action: 'save', files: webFiles });
        return;
      }

      setExportProgress(100);

      trackStudioAction(trackingSource, 'saveImage', {
        id: templateId,
        format: settings.format,
        pages: indexes.length
      });
      toast.info(IconToast('success', intl.formatMessage({ id: 'hot-topic-studio-saved' })), {
        className: 'new-toast'
      });
    } catch (error) {
      console.error(error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'hot-topic-studio-save-failed' })), {
        className: 'new-toast'
      });
    } finally {
      setExporting(false);
    }
  };

  const handleSave = () => {
    trackStudioAction(trackingSource, 'downloaded', { id: templateId });
    downloadPages(pages.map((_page: any, index: number) => index));
  };

  // Web Share needs a direct, current user gesture. PNG rendering happens
  // asynchronously, so the browser rejects navigator.share if it is called
  // only after Lambda has completed. This second click is web-only.
  const openWebShareSheet = async () => {
    if (!pendingWebAction) return;
    const webAction = pendingWebAction;

    // Older iOS WebKit versions can leave navigator.share() pending after a
    // user selects "Save Images". Close our preparation dialog before the
    // system sheet opens, so it cannot remain stuck when the user returns.
    setPendingWebAction(null);

    try {
      const shareData = {
        files: webAction.files
      };

      if (navigator.canShare && !navigator.canShare(shareData)) {
        throw new Error('This browser cannot share image files.');
      }

      await navigator.share(shareData);
      if (webAction.action === 'share') {
        trackStudioAction(trackingSource, 'shared', { id: templateId });
      } else {
        trackStudioAction(trackingSource, 'saveImage', {
          id: templateId,
          format: settings.format,
          pages: pages.length
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error(error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'hot-topic-studio-share-failed' })), {
        className: 'new-toast'
      });
    }
  };

  const shareAllPages = async () => {
    if (!isSharable()) {
      toast.info(IconToast('error', intl.formatMessage({ id: 'hot-topic-studio-share-unsupported' })), {
        className: 'new-toast'
      });
      return;
    }

    trackStudioAction(trackingSource, 'generate', { id: templateId });
    setExportProgress(0);
    setExporting(true);
    const shareTitle = getLocalizedTitle(item, settings.locale) || getLocalizedTitle(item, intl.locale);
    const isWeb = NativeOrWeb.getPlatform() === 'web';
    const imageUrls = [];
    const webFiles: File[] = [];

    try {
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const blobs = await renderPageBlobs(pages.map((_page: any, index: number) => index));
      const exportedAt = new Date();
      const fileNames = blobs.map((blob, index) =>
        getStudioExportFileName(shareTitle || templateId, index + 1, blob.type, exportedAt)
      );
      for (let index = 0; index < blobs.length; index += 1) {
        const blob = blobs[index];
        if (isWeb) {
          webFiles.push(new File([blob], fileNames[index], { type: blob.type || 'image/jpeg' }));
        } else {
          imageUrls.push(URL.createObjectURL(blob));
        }
      }

      if (isWeb) {
        setPendingWebAction({ action: 'share', files: webFiles });
        return;
      }

      try {
        await share({
          title: shareTitle,
          text: shareTitle,
          imageUrls,
          fileNames
        } as any);
        setExportProgress(100);
        trackStudioAction(trackingSource, 'shared', { id: templateId });
      } finally {
        imageUrls.forEach(imageUrl => URL.revokeObjectURL(imageUrl));
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error(error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'hot-topic-studio-share-failed' })), {
        className: 'new-toast'
      });
    } finally {
      setExporting(false);
    }
  };

  if (!useInjectedStudioItem && (!hotTopic || !Array.isArray(hotTopic.data))) {
    return (
      <AppContentContainer className="hot-topic-studio-editor-page" style={PAGE_STYLE}>
        <div className="studio-editor-state embedded">
          <CircularProgress color="primary" />
        </div>
      </AppContentContainer>
    );
  }

  if (!item || !pages.length) {
    return (
      <AppContentContainer className="hot-topic-studio-editor-page" style={PAGE_STYLE}>
        <div className="studio-editor-state embedded">
          <Typography color="textSecondary">{intl.formatMessage({ id: 'hot-topic-studio-not-found' })}</Typography>
          <Button variant="outlined" color="primary" onClick={() => history.push(notFoundBackPath || '/hot_topic_studio')}>
            {intl.formatMessage({ id: 'Back' })}
          </Button>
        </div>
      </AppContentContainer>
    );
  }

  const title = getLocalizedTitle(item, intl.locale);
  const mobilePlatform = NativeOrWeb.getPlatform();
  const mobilePlatformClass = mobilePlatform === 'android' ? ' android' : mobilePlatform === 'web' ? ' web' : '';
  const mobileEditorStyle = { '--studio-footer-nav-height': `${footerNavHeight}px` } as React.CSSProperties;
  const leavePageConfirm = (
    <LeavePageConfirm
      shouldShow={hasUnsavedLayoutChanges}
      save={savePreferences}
      render={({ prompt, open, cancel, exit, saveAndExit }: any) => (
        <>
          {prompt}
          <Dialog
            open={open}
            onClose={cancel}
            {...({
              PaperProps: { className: 'studio-web-share-dialog' },
              BackdropProps: { className: 'studio-web-share-backdrop' }
            } as any)}
          >
            <DialogTitle>{intl.formatMessage({ id: 'hot-topic-studio-leave-title' })}</DialogTitle>
            <DialogContent>
              <Typography>
                {intl.formatMessage({
                  id: selectedSlotIsEmpty
                    ? 'hot-topic-studio-leave-empty-description'
                    : 'hot-topic-studio-leave-description'
                })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button className="studio-web-share-cancel" onClick={cancel}>
                {intl.formatMessage({ id: 'Cancel' })}
              </Button>
              <div style={{ flex: '1 0 0' }} />
              <Button className="studio-web-share-cancel" onClick={exit}>
                {intl.formatMessage({ id: "Don't Save" })}
              </Button>
              <Button className="studio-web-share-confirm" variant="contained" color="primary" onClick={saveAndExit}>
                {intl.formatMessage({ id: 'Save' })}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    />
  );

  const slotSwitchConfirm = (
    <Dialog
      open={pendingSlotSwitch != null}
      onClose={() => setPendingSlotSwitch(null)}
      {...({
        PaperProps: { className: 'studio-web-share-dialog' },
        BackdropProps: { className: 'studio-web-share-backdrop' }
      } as any)}
    >
      <DialogTitle>{intl.formatMessage({ id: 'hot-topic-studio-leave-title' })}</DialogTitle>
      <DialogContent>
        <Typography>
          {intl.formatMessage({
            id: selectedSlotIsEmpty
              ? 'hot-topic-studio-leave-empty-description'
              : 'hot-topic-studio-leave-description'
          })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button className="studio-web-share-cancel" onClick={() => setPendingSlotSwitch(null)}>
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
        <div style={{ flex: '1 0 0' }} />
        <Button
          className="studio-web-share-cancel"
          onClick={() => {
            const nextSlot = pendingSlotSwitch;
            setPendingSlotSwitch(null);
            if (nextSlot != null) {
              applyPreferenceSlot(nextSlot);
            }
          }}
        >
          {intl.formatMessage({ id: "Don't Save" })}
        </Button>
        <Button
          className="studio-web-share-confirm"
          variant="contained"
          color="primary"
          onClick={() => {
            const nextSlot = pendingSlotSwitch;
            setPendingSlotSwitch(null);
            if (nextSlot != null) {
              const nextStore = savePreferences();
              applyPreferenceSlot(nextSlot, nextStore);
            }
          }}
        >
          {intl.formatMessage({ id: 'Save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isMobile) {
    return (
      <AppContentContainer className="hot-topic-studio-editor-page" style={PAGE_STYLE}>
        {leavePageConfirm}
        {slotSwitchConfirm}
        <div className={`hot-topic-studio-editor mobile${mobilePlatformClass}`} style={mobileEditorStyle}>
          <header className="studio-mobile-header">
            <div className="studio-mobile-heading">
              <Typography className="studio-mobile-title" noWrap>
                {title}
              </Typography>
            </div>
            <div className="studio-header-actions">
              <IconButton
                color="primary"
                aria-label={intl.formatMessage({ id: 'Share' })}
                disabled={exporting}
                onClick={shareAllPages}
              >
                <ShareIcon />
              </IconButton>
              <IconButton
                color="primary"
                aria-label={intl.formatMessage({ id: 'hot-topic-studio-save-image' })}
                disabled={exporting}
                onClick={handleSave}
              >
                <SaveAltIcon />
              </IconButton>
            </div>
          </header>

          <div className="studio-mobile-body">
            <main className="studio-mobile-canvas-workspace">
              <Slider
                ref={sliderRef}
                dots={false}
                arrows={false}
                infinite={false}
                speed={260}
                swipe
                afterChange={setPageIndex}
              >
                {pages.map((page: any, index: number) => (
                  <div className="studio-mobile-slide" key={`${page.layoutType}-${index}`}>
                    <StudioCanvas page={page} settings={renderSettings} pageNumber={index + 1} />
                  </div>
                ))}
              </Slider>
              <MobileToolBar
                settings={renderSettings}
                onChangeSettings={updateSettings}
                preferencesStore={preferencesStore}
                selectedSlot={selectedSlot}
                slotName={slotName}
                onSelectSlot={selectPreferenceSlot}
                onChangeSlotName={changeSlotName}
                onRenameSlot={renamePreferenceSlot}
                onSavePreferences={savePreferences}
                onClearPreferences={clearPreferenceSlot}
                hideProfile={hideProfile}
              />
            </main>
            {pages.length > 1 && (
              <div className="studio-mobile-dock">
                <PageNavigator
                  pages={pages}
                  pageIndex={pageIndex}
                  settings={renderSettings}
                  onChangePage={changePage}
                  variant="mobile"
                />
              </div>
            )}
          </div>

          <StudioExportLoading
            intl={intl}
            open={exporting}
            progress={exportProgress}
          />
          <WebShareReadyDialog
            intl={intl}
            open={Boolean(pendingWebAction)}
            action={pendingWebAction && pendingWebAction.action}
            onClose={() => setPendingWebAction(null)}
            onShare={openWebShareSheet}
          />
        </div>
      </AppContentContainer>
    );
  }

  return (
    <AppContentContainer className="hot-topic-studio-editor-page" style={PAGE_STYLE}>
      {leavePageConfirm}
      {slotSwitchConfirm}
      <div className="hot-topic-studio-editor desktop">
        <header className="studio-desktop-header">
          <div className="studio-desktop-heading">
            <Typography variant="h6" noWrap>
              {title}
            </Typography>
          </div>
          <div className="studio-header-actions">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ShareIcon />}
              disabled={exporting}
              onClick={shareAllPages}
            >
              {intl.formatMessage({ id: 'Share' })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveAltIcon />}
              disabled={exporting}
              onClick={handleSave}
            >
              {intl.formatMessage({ id: 'hot-topic-studio-save-image' })}
            </Button>
          </div>
        </header>

        <main className="studio-desktop-body">
          <section className="studio-canvas-column">
            <div className="studio-canvas-workspace">
              <Slider
                ref={sliderRef}
                dots={false}
                arrows={false}
                infinite={false}
                speed={260}
                swipe
                swipeToSlide
                afterChange={setPageIndex}
              >
                {pages.map((page: any, index: number) => (
                  <div className="studio-desktop-slide" key={`${page.layoutType}-${index}`}>
                    <StudioCanvas page={page} settings={renderSettings} pageNumber={index + 1} />
                  </div>
                ))}
              </Slider>
            </div>
            <PageNavigator pages={pages} pageIndex={pageIndex} settings={renderSettings} onChangePage={changePage} />
          </section>
          <aside className="studio-sidebar-column">
            <StudioSidebar
              settings={renderSettings}
              onChangeSettings={updateSettings}
              preferencesStore={preferencesStore}
              selectedSlot={selectedSlot}
              slotName={slotName}
              onSelectSlot={selectPreferenceSlot}
              onChangeSlotName={changeSlotName}
              onRenameSlot={renamePreferenceSlot}
              onSavePreferences={savePreferences}
              onClearPreferences={clearPreferenceSlot}
              hideProfile={hideProfile}
            />
          </aside>
        </main>

        <StudioExportLoading
          intl={intl}
          open={exporting}
          progress={exportProgress}
        />
        <WebShareReadyDialog
          intl={intl}
          open={Boolean(pendingWebAction)}
          action={pendingWebAction && pendingWebAction.action}
          onClose={() => setPendingWebAction(null)}
          onShare={openWebShareSheet}
        />
      </div>
    </AppContentContainer>
  );
};

export default StudioEditor;
